import { ContentDeclaration, ordinaryContent } from "@/components/ContentConsents";
import { PrivateImage } from "@/components/PrivateMedia";
import React, { useState } from "react";
import { Star, Trash2, Upload, MoveUp } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAX = 8;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

export default function PhotoManager({ photos = [], onChange }) {
  const { lang } = useI18n();
  const [uploading, setUploading] = useState(false);
  const [contentContext,setContentContext]=useState(ordinaryContent);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const T = {
    add: lang === "it" ? "Aggiungi foto" : "Add photo",
    main: lang === "it" ? "Principale" : "Main",
    setMain: lang === "it" ? "Rendi principale" : "Set as main",
    remove: lang === "it" ? "Rimuovi" : "Remove",
    dropHint: lang === "it" ? "JPG, PNG o WebP · max 10 MB" : "JPG, PNG or WebP · max 10 MB",
    count: lang === "it" ? "foto" : "photos",
    tooMany: lang === "it" ? `Massimo ${MAX} foto.` : `Maximum ${MAX} photos.`,
    invalidType: lang === "it" ? "Formato non supportato." : "Unsupported file type.",
    tooLarge: lang === "it" ? "File troppo grande (max 10 MB)." : "File too large (max 10 MB).",
    uploading: lang === "it" ? "Caricamento…" : "Uploading…",
    genericFail: lang === "it" ? "Non è stato possibile caricare la foto. Riprova." : "We couldn't upload the photo. Please try again.",
  };

  const localizeError = (raw) => {
    const map = {
      it: {
        unsupported_file_type: T.invalidType,
        file_too_large: T.tooLarge,
        invalid_image: lang === "it" ? "L'immagine non è valida." : "The image is not valid.",
        max_photos_reached: T.tooMany,
        storage_error: T.genericFail,
        photo_not_found: lang === "it" ? "Foto non trovata." : "Photo not found.",
      },
      en: {
        unsupported_file_type: T.invalidType,
        file_too_large: T.tooLarge,
        invalid_image: "The image is not valid.",
        max_photos_reached: T.tooMany,
        storage_error: T.genericFail,
        photo_not_found: "Photo not found.",
      },
    };
    if (typeof raw === "string" && map[lang][raw]) return map[lang][raw];
    return T.genericFail;
  };

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { setError(T.invalidType); return; }
    if (file.size > 10 * 1024 * 1024) { setError(T.tooLarge); return; }
    if ((photos?.length || 0) >= MAX) { setError(T.tooMany); return; }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("content_context",JSON.stringify(contentContext));
      form.append("file", file);
      const { data } = await api.post("/profile/photos/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      onChange?.(data.photos);
    } catch (e) {
      setError(localizeError(extractError(e)));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const setMain = async (id) => {
    try {
      const { data } = await api.patch("/profile/photos/main", { photo_id: id });
      onChange?.(data.photos);
    } catch (e) {
      setError(extractError(e));
    }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/profile/photos/${id}`);
      onChange?.(data.photos);
    } catch (e) {
      setError(extractError(e));
    }
  };

  const buildUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);
  const sorted = [...(photos || [])].sort((a, b) => (a.is_main ? -1 : b.is_main ? 1 : 0));

  return (
    <div data-testid="photo-manager">
      <ContentDeclaration scope="profile" value={contentContext} onChange={setContentContext} disabled={uploading} />
      {error ? (
        <div className="mb-4 text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="photo-error">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {sorted.map((p) => (
          <div
            key={p.id}
            className="relative aspect-[3/4] rounded-md overflow-hidden group"
            style={{ border: "1px solid hsl(var(--nvl-border))" }}
            data-testid={`photo-tile-${p.id}`}
          >
            <PrivateImage src={buildUrl(p.url)} alt="" className="w-full h-full object-contain" />
            {p.is_main ? (
              <span
                className="absolute top-2 left-2 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest"
                style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
              >
                <Star size={10} className="inline mr-1" /> {T.main}
              </span>
            ) : null}
            <div className="absolute inset-x-0 bottom-0 flex gap-1 p-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.55))" }}>
              {!p.is_main ? (
                <button
                  onClick={() => setMain(p.id)}
                  data-testid={`photo-set-main-${p.id}`}
                  className="flex-1 text-[10px] px-2 py-1 rounded"
                  style={{ backgroundColor: "hsl(var(--nvl-bg))", color: "hsl(var(--nvl-ink))" }}
                >
                  <Star size={10} className="inline mr-1" />
                  {T.setMain}
                </button>
              ) : null}
              <button
                onClick={() => remove(p.id)}
                data-testid={`photo-remove-${p.id}`}
                className="text-[10px] px-2 py-1 rounded"
                style={{ backgroundColor: "hsl(var(--destructive))", color: "hsl(var(--nvl-bg))" }}
              >
                <Trash2 size={10} className="inline mr-1" />
                {T.remove}
              </button>
            </div>
          </div>
        ))}

        {photos.length < MAX ? (
          <label
            className="relative aspect-[3/4] rounded-md flex flex-col items-center justify-center cursor-pointer text-center px-4"
            style={{ border: "1px dashed hsl(var(--nvl-border))", color: "hsl(var(--nvl-ink-soft))" }}
            data-testid="photo-add-tile"
          >
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={uploading}
              data-testid="photo-input"
            />
            <Upload size={18} />
            <span className="mt-2 text-xs">{uploading ? `${T.uploading} ${progress}%` : T.add}</span>
            <span className="mt-1 text-[10px]">{T.dropHint}</span>
          </label>
        ) : null}
      </div>

      <p className="mt-3 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }} data-testid="photo-count">
        {photos.length} / {MAX} {T.count}
      </p>
    </div>
  );
}
